import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { buildApiUrl } from "../utils/apiConfig";
import "../styles/add-college-modal.css";

interface College {
    id: number;
    name: string;
    city: string;
    state: string;
    zip: string;
    schoolUrl: string;
    locale: string;
    size: number;
}

interface AddCollegeModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAddCollege: (collegeName: string) => void;
    athleteUsername?: string;
}

export default function AddCollegeModal({ isOpen, onClose, onAddCollege, athleteUsername }: AddCollegeModalProps) {
    const [colleges, setColleges] = useState<College[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCollege, setSelectedCollege] = useState<College | null>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    /** Brief read-only on open stops Chrome/Safari OTP autofill from covering the dropdown. */
    const [blockAutofill, setBlockAutofill] = useState(true);

    const PAGE_SIZE = 25;

    useEffect(() => {
        if (isOpen) {
            setBlockAutofill(true);
            const frame = requestAnimationFrame(() => {
                searchInputRef.current?.focus({ preventScroll: true });
            });
            return () => cancelAnimationFrame(frame);
        } else {
            // Reset state when modal closes
            setSearchQuery("");
            setSelectedCollege(null);
            setColleges([]);
            setPage(0);
            setHasMore(true);
            setIsDropdownOpen(false);
            setSubmitError(null);
        }
    }, [isOpen]);

    useEffect(() => {
        // Close dropdown when clicking outside
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const fetchColleges = async (name: string, pageNum: number, append: boolean = false) => {
        try {
            setLoading(true);

            const params = new URLSearchParams({
                page: pageNum.toString(),
                size: PAGE_SIZE.toString()
            });
            if (name) {
                params.append("name", name);
            }

            const response = await fetch(buildApiUrl(`/api/colleges?${params.toString()}`), {
                headers: { Accept: "application/json" },
                credentials: "include" // Use HTTP-only cookie for auth
            });

            if (response.ok) {
                const data = await response.json();

                // Handle both array response and PageResponse object
                let collegeList: College[];
                let isLastPage: boolean;

                if (Array.isArray(data)) {
                    // Backend returned a plain array
                    collegeList = data;
                    // Assume more pages exist if we got a full page
                    isLastPage = data.length < PAGE_SIZE;
                } else {
                    // Backend returned a PageResponse object
                    collegeList = data.content || [];
                    isLastPage = data.last ?? true;
                }

                if (append) {
                    setColleges(prev => [...prev, ...collegeList]);
                } else {
                    setColleges(collegeList);
                }

                setHasMore(!isLastPage);
                setPage(pageNum);
            } else {
                console.error("Failed to fetch colleges:", response.status);
                setColleges([]);
                setHasMore(false);
            }
        } catch (error) {
            console.error("Error fetching colleges:", error);
            setColleges([]);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchQuery(value);
        setIsDropdownOpen(true);
        setSelectedCollege(null);

        // Debounce search
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            fetchColleges(value, 0);
        }, 300);
    };

    const handleCollegeSelect = (college: College) => {
        setSelectedCollege(college);
        setSearchQuery(college.name);
        setIsDropdownOpen(false);
    };

    const handleLoadMore = () => {
        if (!loading && hasMore) {
            fetchColleges(searchQuery, page + 1, true);
        }
    };

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop <= clientHeight * 1.5 && hasMore && !loading) {
            handleLoadMore();
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError(null);
        if (!selectedCollege) {
            return;
        }
        if (!athleteUsername?.trim()) {
            setSubmitError("We couldn't add this college because your account isn't ready yet. Please refresh the page and try again.");
            return;
        }
        try {
            const response = await fetch(
                buildApiUrl(`/api/colleges/athlete/${encodeURIComponent(athleteUsername)}`),
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json"
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        id: selectedCollege.id.toString(),
                        name: selectedCollege.name,
                        city: selectedCollege.city,
                        state: selectedCollege.state,
                        zip: selectedCollege.zip,
                        schoolUrl: selectedCollege.schoolUrl,
                        locale: selectedCollege.locale,
                        size: selectedCollege.size
                    })
                }
            );

            if (response.ok) {
                onAddCollege(selectedCollege.name);
                setSearchQuery("");
                setSelectedCollege(null);
                return;
            }
            setSubmitError("We couldn't add this college. Please try again.");
        } catch {
            setSubmitError("We couldn't add this college. Please try again.");
        }
    };

    const handleClose = () => {
        setSearchQuery("");
        setSelectedCollege(null);
        onClose();
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="add-college-modal-overlay">
            <div className="modal-content add-college-modal">
                <div className="modal-header">
                    <h2 className="modal-title">Add college</h2>
                    <button className="modal-close-btn" onClick={handleClose}>
                        ✕
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="modal-form" autoComplete="off">
                    <div className="modal-body">
                        {submitError && (
                            <div className="add-college-error" role="alert">
                                {submitError}
                            </div>
                        )}
                        <div className="college-search-container" ref={dropdownRef}>
                            <input
                                ref={searchInputRef}
                                id="add-college-search"
                                type="search"
                                className="college-name-input"
                                name="college-search-query"
                                placeholder="Enter college name"
                                value={searchQuery}
                                onChange={handleSearchChange}
                                onFocus={() => {
                                    setBlockAutofill(false);
                                    if (colleges.length > 0) {
                                        setIsDropdownOpen(true);
                                    }
                                }}
                                onClick={() => {
                                    setBlockAutofill(false);
                                    if (colleges.length === 0 && !loading) {
                                        fetchColleges("", 0);
                                    }
                                    setIsDropdownOpen(true);
                                }}
                                readOnly={blockAutofill}
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck={false}
                                inputMode="search"
                                enterKeyHint="search"
                                role="combobox"
                                aria-label="College name search"
                                aria-autocomplete="list"
                                aria-expanded={isDropdownOpen}
                                aria-controls="add-college-search-listbox"
                                data-1p-ignore
                                data-lpignore="true"
                            />

                            {isDropdownOpen && (
                                <div
                                    id="add-college-search-listbox"
                                    className="college-dropdown"
                                    role="listbox"
                                    onScroll={handleScroll}
                                >
                                    {loading && colleges.length === 0 ? (
                                        <div className="dropdown-item loading">Loading...</div>
                                    ) : colleges.length === 0 ? (
                                        <div className="dropdown-item no-results">No colleges found</div>
                                    ) : (
                                        <>
                                            {colleges.map((college) => (
                                                <div
                                                    key={college.id}
                                                    className="dropdown-item"
                                                    role="option"
                                                    onClick={() => handleCollegeSelect(college)}
                                                >
                                                    <div className="college-name">{college.name}</div>
                                                    <div className="college-location">
                                                        {college.city}, {college.state}
                                                    </div>
                                                </div>
                                            ))}
                                            {loading && <div className="dropdown-item loading">Loading more...</div>}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="add-college-modal-footer">
                        <button
                            type="submit"
                            className="add-college-submit-btn"
                            disabled={!selectedCollege}
                        >
                            Add college to my list
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
}
